import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { memberPermissionsKeys } from "../roles.utils"
import type { RevokeMemberPermissionParams } from "./useRevokeMemberPermission.types"

/**
 * Organization-specific hook for revoking a direct permission from a member
 * Can only be used in mimber-organization app
 */
export const useRevokeMemberPermission = (
  memberUuid: string,
  options?: UseMutationOptions<void, Error, RevokeMemberPermissionParams>
): UseMutationResult<void, Error, RevokeMemberPermissionParams> => {
  guardAppUsage("mimber-organization", "useRevokeMemberPermission")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ permissionUuid }: RevokeMemberPermissionParams) => {
      await server.delete(`members/${memberUuid}/permissions/${permissionUuid}`)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: memberPermissionsKeys.findByMember(memberUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
